import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { shiftDays, type Dataset } from "@otrack/domain";
import { Icon, Tooltip } from "@otrack/ui";
import { projectLabel } from "../commands";
import { formatClock, formatDuration, HOUR } from "../time";
import type { Draft } from "./actions";
import { colorOf, isEditablePlan, planTone, READ_ONLY, trackedIn, type CalItem, type EntryItem, type PlanItem } from "./model";
import { PlanGlyph, type MenuTarget } from "./time-grid";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const longDay = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" });
const MAX_CHIPS = 3;

/** Time per Project in a day, largest first, for the stacked bar. */
function projectShares(entries: readonly EntryItem[], from: number, to: number): Array<{ projectId: string | null; ms: number }> {
  const totals = new Map<string | null, number>();
  for (const entry of entries) {
    const ms = Math.max(0, Math.min(entry.end, to) - Math.max(entry.start, from));
    if (ms) totals.set(entry.projectId, (totals.get(entry.projectId) ?? 0) + ms);
  }
  return [...totals].map(([projectId, ms]) => ({ projectId, ms })).sort((a, b) => b.ms - a.ms);
}

export function MonthGrid({ weeks, month, entries, plans, dataset, now, selection, onSelect, onOpenItem, onEditItem, onDay, onDraft, onMenu, shiftItems, notify, draftKey }: {
  weeks: number[][];
  month: number;
  entries: EntryItem[];
  plans: PlanItem[];
  dataset: Dataset;
  now: number;
  selection: ReadonlySet<string>;
  onSelect: (keys: Set<string>) => void;
  onOpenItem: (key: string) => void;
  onEditItem: (item: CalItem) => void;
  onDay: (day: number) => void;
  onDraft: (draft: Draft) => void;
  onMenu: (x: number, y: number, target: MenuTarget) => void;
  shiftItems: (items: readonly CalItem[], delta: number, duplicate: boolean, dayDelta?: number) => Promise<boolean>;
  notify: (message: string) => void;
  draftKey: number | null;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const [drag, setDrag] = useState<{ type: "move"; item: PlanItem; from: number; to: number; moved: boolean } | { type: "range"; from: number; to: number; moved: boolean } | null>(null);
  const days = weeks.flat();

  const dayAt = (x: number, y: number): number | null => {
    const cell = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-cell]");
    return cell ? Number(cell.dataset["cell"]) : null;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.pointerType === "touch") return;
    const target = event.target as HTMLElement;
    if (target.closest("button")) return;
    const day = dayAt(event.clientX, event.clientY);
    if (day === null) return;
    const key = target.closest<HTMLElement>("[data-key]")?.dataset["key"];
    const item = key ? plans.find((plan) => plan.key === key) : undefined;
    start.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag(item ? { type: "move", item, from: day, to: day, moved: false } : { type: "range", from: day, to: day, moved: false });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || !start.current) return;
    const moved = drag.moved || Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y) > 4;
    const day = dayAt(event.clientX, event.clientY) ?? drag.to;
    setDrag({ ...drag, to: day, moved });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag;
    setDrag(null);
    if (!current) return;
    if (current.type === "move") {
      if (!current.moved) {
        onSelect(new Set([current.item.key]));
        return onOpenItem(current.item.key);
      }
      if (current.to === current.from) return;
      if (!isEditablePlan(current.item) && !event.altKey) return notify(READ_ONLY);
      const dayDelta = Math.round((current.to - current.from) / 86_400_000);
      void shiftItems([current.item], shiftDays(current.item.start, dayDelta) - current.item.start, event.altKey, dayDelta);
      return;
    }
    if (!current.moved || current.from === current.to) return onDay(current.from);
    const from = Math.min(current.from, current.to), to = Math.max(current.from, current.to);
    onDraft({ kind: "event", start: from, end: shiftDays(to, 1), allDay: true });
  };

  const range = drag?.type === "range" && drag.moved ? { from: Math.min(drag.from, drag.to), to: Math.max(drag.from, drag.to) } : null;
  const target = drag?.type === "move" && drag.moved ? drag.to : null;

  return <div className="ot-month-cal" role="grid" aria-label="Month">
    <div className="ot-month-cal__weekdays" role="row">{WEEKDAYS.map((name) => <span key={name} role="columnheader">{name}</span>)}</div>
    <div
      ref={gridRef}
      className="ot-month-cal__grid"
      style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDrag(null)}
      onContextMenu={(event) => {
        event.preventDefault();
        const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset["key"];
        const item = key ? plans.find((plan) => plan.key === key) ?? null : null;
        const day = dayAt(event.clientX, event.clientY);
        onMenu(event.clientX, event.clientY, { item, slot: item || day === null ? null : { day, minute: -1 } });
      }}
    >
      {days.map((day) => {
        const next = shiftDays(day, 1);
        const outside = new Date(day).getMonth() !== new Date(month).getMonth();
        const today = now >= day && now < next;
        const tracked = trackedIn(entries, day, next);
        const shares = projectShares(entries, day, next);
        const dayPlans = plans.filter((plan) => plan.start < next && (plan.end > day || (plan.start === plan.end && plan.start >= day)))
          .sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start - b.start);
        const hidden = Math.max(0, dayPlans.length - MAX_CHIPS);
        const inRange = range && day >= range.from && day <= range.to;
        const scale = Math.max(8 * HOUR, tracked);
        return <div
          key={day}
          data-cell={day}
          role="gridcell"
          aria-label={`${longDay.format(day)}, ${formatDuration(tracked)} tracked, ${dayPlans.length} planned`}
          className={`ot-month-cal__cell ${outside ? "is-outside" : ""} ${today ? "is-today" : ""} ${inRange ? "is-range" : ""} ${target === day ? "is-target" : ""} ${draftKey === day ? "is-range" : ""}`}
        >
          <div className="ot-month-cal__top">
            <button type="button" className="ot-month-cal__date" onClick={() => onDay(day)} aria-label={`Open ${longDay.format(day)}`}>{new Date(day).getDate()}</button>
            {tracked > 0 && <span className="ot-month-cal__tracked ot-mono">{formatDuration(tracked)}</span>}
          </div>
          {tracked > 0 && <Tooltip label={shares.map((share) => `${projectLabel(dataset, share.projectId)}: ${formatDuration(share.ms)}`).join("\n")} delay={120}>
            <div className="ot-month-cal__bar" style={{ width: `${Math.max(6, tracked / scale * 100)}%` }}>
              {shares.map((share) => <i key={share.projectId ?? "none"} style={{ flexGrow: share.ms, background: colorOf(dataset, share.projectId) }}/>)}
            </div>
          </Tooltip>}
          <div className="ot-month-cal__chips">
            {dayPlans.slice(0, MAX_CHIPS).map((plan) => <div
              key={plan.key}
              data-key={plan.key}
              role="button"
              tabIndex={0}
              aria-pressed={selection.has(plan.key)}
              aria-label={`${plan.title || "Untitled"}${plan.allDay ? ", all day" : `, ${formatClock(plan.start)}`}`}
              className={`ot-month-cal__chip ${plan.allDay ? "is-allday" : ""} ot-month-cal__chip--${planTone(plan)} ${selection.has(plan.key) ? "is-selected" : ""} ${plan.end <= now && !plan.logged.length ? "is-past" : ""} ${drag?.type === "move" && drag.moved && drag.item.key === plan.key ? "is-lifted" : ""}`}
              style={{ ["--pc" as string]: colorOf(dataset, plan.projectId), ...(plan.google ? { ["--gc" as string]: plan.google.calendar.color } : {}) }}
              onDoubleClick={() => onEditItem(plan)}
            >
              {!plan.allDay && <span className="ot-mono">{formatClock(plan.start)}</span>}
              {plan.logged.length > 0 ? <Icon name="check-circle" weight="bold" className="ot-cb__glyph ot-cb__glyph--done"/> : <PlanGlyph plan={plan}/>}
              <span className="ot-month-cal__chip-label">{plan.title || "Untitled"}</span>
            </div>)}
            {hidden > 0 && <button type="button" className="ot-month-cal__more" onClick={() => onDay(day)}>+{hidden} more</button>}
          </div>
        </div>;
      })}
    </div>
  </div>;
}
